import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import { films, filmCategories } from "@/mocks/films";

export default function CollectionSection() {
  const [active, setActive] = useState("all");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return films.filter((f) => {
      const matchCat = active === "all" || f.category === active;
      if (!matchCat) return false;
      if (!kw) return true;
      return (
        f.title.toLowerCase().includes(kw) ||
        f.original.toLowerCase().includes(kw) ||
        String(f.year).includes(kw) ||
        f.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
  }, [active, keyword]);

  return (
    <section id="collection" className="py-16 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div className="max-w-2xl">
              <span className="text-xs md:text-sm font-label tracking-widest text-primary-600 uppercase">Collection</span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">精选片单：按主题找一部想看的</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                下面这些条目都标注了出品年份与地区，并按主题归类。你可以直接切换标签，或输入片名、年份、
                关键词来缩小范围，找到此刻最适合的一部。
              </p>
            </div>
            <div className="relative w-full md:w-72 shrink-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索片名、年份或关键词"
                className="w-full pl-10 pr-9 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => setKeyword("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-200 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-8 flex flex-wrap gap-2">
            {filmCategories.map((c) => {
              const isActive = active === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setActive(c.key)}
                  aria-pressed={isActive}
                  className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <p className="mt-5 text-xs md:text-sm text-foreground-500">
          当前共 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
          {keyword ? `（关键词：${keyword}）` : ""}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((f, i) => (
              <Reveal key={f.id} delay={(i % 3) * 70}>
                <article className="h-full rounded-lg border border-background-200 bg-background-100 p-5 flex flex-col hover:border-primary-300 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950">{f.title}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{f.original}</p>
                    </div>
                    <span className="shrink-0 px-2.5 py-1 rounded-md bg-foreground-950 text-background-50 text-xs font-label">
                      {f.year}
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-relaxed text-foreground-600 flex-1">{f.note}</p>

                  <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-secondary-700">
                      <i className="ri-map-pin-line"></i>
                      {f.region}
                    </span>
                    <ul className="flex flex-wrap justify-end gap-1.5">
                      {f.tags.map((t) => (
                        <li key={t} className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs">
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的条目，换个关键词或切回“全部”试试。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActive("all");
              }}
              className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}